import {
  Call,
  Coffee,
  DocumentText,
  Edit2,
  Sms,
  TickCircle,
  UserMinus,
  UserSearch,
} from "iconsax-react";
import React, { useEffect, useRef } from "react";
import { Draggable } from "react-beautiful-dnd";
import Can from "../Can";

const DraggableGuestCard = ({
  index,
  seatIndex,
  data,
  tableId,
  activeGuestId,
  setActiveGuestId,
  pendingMoves,
  handleViewGuestDetails,
  openModal,
  isDraggedOver,
  highlightRed = false,
}) => {
  const menuRef = useRef(null);
  const isActive = activeGuestId === data.guest_id;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveGuestId(null);
      }
    };

    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isActive, setActiveGuestId]);

  const baseClass =
    "relative flex w-full p-[16px] font-normal text-[14px] gap-2 border border-transparent";
  return (
    <Draggable
      draggableId={`${data.guest_id}`}
      key={data.guest_id}
      index={index}
    >
      {(provided, snapshot) => {
        const isSwapTarget = isDraggedOver && !snapshot.isDragging;
        const offsetClass = isSwapTarget ? "-mt-[42px]" : "";
        const baseStateClass = pendingMoves.some(
          (move) => move.guestId === data.guest_id,
        )
          ? `${baseClass} bg-[#F7F6FF] border-[#5C4BF5]`
          : highlightRed
            ? `${baseClass} bg-[#FFF5F5] border-[#FF3030] hover:bg-[#FFF0F0]`
            : `${baseClass} bg-white hover:bg-[#F7F8FA]`;

        const finalClass = isSwapTarget
          ? `${baseClass} ${offsetClass} bg-[#EFEDFE] outline outline-2 outline-[#5C4BF5] z-10 transition-all shadow-md`
          : baseStateClass;

        return (
          <div
            className={finalClass}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
          >
            <div
              className={`flex w-full gap-2 ${isSwapTarget ? "seat-wiggle" : ""}`}
            >
              {tableId > 0 && (
                <h2>{seatIndex !== undefined ? seatIndex + 1 : index + 1}.</h2>
              )}
              <div className="flex flex-col w-full gap-2">
                <div className="flex w-full font-normal items-center">
                  <div className="flex flex-col w-full">
                    <h2 className="font-medium">
                      {data.salutation && `${data.salutation}. `}
                      {data.guest_name}
                    </h2>
                    {data.replaced_by && (
                      <div className="flex gap-[5px]">
                        <h2>
                          <span className="text-purple-500">replaced by</span>{" "}
                          {data.replaced_by}
                        </h2>
                      </div>
                    )}
                  </div>
                  {data.status && (
                    <div className="flex w-fit rounded-md px-[8px] gap-[4px] items-center bg-[#F4FCF7] border border-[#E9F9EF] text-[#22C55E] text-[11px] mr-[24px]">
                      <TickCircle size="11" variant="Bold" />
                      Checked
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2 text-[#636C7E]">
                    {[
                      { icon: Sms, value: data.email },
                      { icon: Call, value: data.contact },
                      { icon: DocumentText, value: data.group_name },
                      {
                        icon: Coffee,
                        value: data.dietary?.length
                          ? data.dietary.map((d) => d.name).join(", ")
                          : null,
                      },
                    ]
                      .filter((item) => item.value)
                      .map(({ icon: Icon, value }, idx) => (
                        <div key={idx} className="flex items-center gap-[2px]">
                          <Icon size="12" variant="Bold" />
                          <p>{value}</p>
                        </div>
                      ))}
                  </div>

                  {data.tags && data.tags.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {data.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 rounded-md bg-[#EFEDFE] text-[#5C4BF5] text-[11px] font-medium"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {tableId > 0 && (
                <div
                  className="absolute top-4 right-4 cursor-pointer"
                  ref={menuRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveGuestId(isActive ? null : data.guest_id);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#000"
                      d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0-4 0m0-6a2 2 0 1 0 4 0a2 2 0 0 0-4 0m0 12a2 2 0 1 0 4 0a2 2 0 0 0-4 0"
                    />
                  </svg>
                  {isActive && (
                    <Can permission="edit_guest">
                      <div className="absolute flex flex-col bg-white rounded-lg border border-[#EFF0F2] shadow-lg top-[24px] right-0 p-[6px] z-50 w-[160px] gap-1">
                        <button
                          className="flex w-full p-2 items-center gap-2 hover:bg-[#F7F8FA]"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal("edit_guest", data);
                            setActiveGuestId(null);
                          }}
                        >
                          <Edit2 size="20" />
                          Edit Guest
                        </button>
                        <button
                          className="flex w-full p-2 items-center gap-2 hover:bg-[#F7F8FA]"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewGuestDetails();
                            setActiveGuestId(null);
                          }}
                        >
                          <UserSearch size="20" />
                          View Details
                        </button>
                        <button
                          className="text-[#FF3030] flex w-full p-2 items-center gap-2 hover:bg-[#F7F8FA]"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal("unseat_individual", data);
                            setActiveGuestId(null);
                          }}
                        >
                          <UserMinus size="20" />
                          Unseat Guest
                        </button>
                      </div>
                    </Can>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }}
    </Draggable>
  );
};

export default DraggableGuestCard;
